import { PropTypes } from "prop-types";
import { useMemo } from "react";
import { GatsbyImage } from "gatsby-plugin-image";

import useResponsiveWithHydrationFix from "../../hooks/useResponsiveWithHydrationFix";
import { ALButton, ALLink, ALVideo } from "../ALComponents";
import { gaCustomEvent } from "../../context/helpers";

import * as Styles from "./banner/styles.module.scss";

function Banner({ content }) {
  const onLinkClick = (linkTitle) => {
    gaCustomEvent("Main Block Link", "Click", linkTitle);
  };

  const imageSource = useResponsiveWithHydrationFix(
    content.img.mobileImage.gatsbyImageData,
    content.img.desktopImage.gatsbyImageData
  );

  const videoId = useResponsiveWithHydrationFix(content.mobileVideoId, content.desktopVideoId);
  const videoData = useResponsiveWithHydrationFix(content.mobileVideo, content.desktopVideo);

  const backgroundColor = useResponsiveWithHydrationFix(
    content.backgroundColors?.mobile,
    content.backgroundColors?.desktop
  );

  const titleTextColor = useResponsiveWithHydrationFix(
    content.titleTextColor?.mobile,
    content.titleTextColor?.desktop
  );

  const titleBackgroundColor = useResponsiveWithHydrationFix(
    content.titleBgColor?.mobile,
    content.titleBgColor?.desktop
  );

  const subtitleClasses = content?.subtitleSize === "Big" ? "h1 h1--bold" : "subtext subtext--bold";
  const subtitleTextColor = useResponsiveWithHydrationFix(
    content.subtitleTextColor?.mobile,
    content.subtitleTextColor?.desktop
  );
  const subtitleBackgroundColor = useResponsiveWithHydrationFix(
    content.subtitleBgColor?.mobile,
    content.subtitleBgColor?.desktop
  );

  const videoThumbnailData = useMemo(
    () => ({
      mobile: content?.mobileVideo?.thumbnail,
      desktop: content?.desktopVideo?.thumbnail,
      hasData: !!content?.mobileVideo?.thumbnail && !!content?.desktopVideo?.thumbnail,
    }),
    [content?.mobileVideo?.thumbnail, content?.desktopVideo?.thumbnail]
  );

  const subtitlePositionClassName = `subtitle__${
    content?.subtitlePosition?.toLowerCase().split(" ")[0]
  }`;

  const hasVideo = (!!videoData && videoData !== "null") || (!!videoId && videoId !== "null");

  if ((!imageSource && !hasVideo) || !content) {
    return null;
  }

  const titleClasses = content?.titleSize === "Big" ? "h1 h1--bold" : "text text--bold";
  let justifyContent = "center";
  if (content.contentVerticalPosition?.toLowerCase() === "top") {
    justifyContent = "start";
  }
  if (content.contentVerticalPosition?.toLowerCase() === "bottom") {
    justifyContent = "end";
  }

  const contentClassNames = [
    Styles.banner_content,
    Styles[subtitlePositionClassName],
    content.contentHorizontalPosition === "Center"
      ? Styles.banner_content
      : Styles.banner_content__left,
    content.contentPosition === "Below Image" ? Styles.belowImage : "",
  ].join(" ");

  return (
    <div
      className={`
        ${Styles.banner}
        id-${content.contentful_id}`}
      style={{ backgroundColor }}
    >
      {hasVideo && (
        <ALVideo
          videoId={videoId}
          videoUrl={videoData?.video?.url}
          customClasses={{
            image_thumbnail: Styles.banner_video__thumbnail,
            container: Styles.banner_video__container,
          }}
          videoThumbnailData={videoThumbnailData}
        />
      )}
      {!hasVideo && (
        <>
          <div className={`${Styles.banner_image} ${Styles.banner_imageMobile}`}>
            <GatsbyImage
              fetchPriority="high"
              image={content.img.mobileImage.gatsbyImageData}
              alt={content.img.altText}
              className={Styles.banner_image__gatsby}
            />
          </div>
          <div className={`${Styles.banner_image} ${Styles.banner_imageDesktop}`}>
            <GatsbyImage
              fetchPriority="high"
              image={content.img.desktopImage.gatsbyImageData}
              alt={content.img.altText}
              className={Styles.banner_image__gatsby}
            />
          </div>
        </>
      )}

      <div className={contentClassNames} style={{ justifyContent }}>
        {content.title && content.title !== "null" && (
          <p
            className={`${Styles.banner_content__title} ${titleClasses}`}
            style={{ color: titleTextColor, backgroundColor: titleBackgroundColor }}
          >
            {content.title}
          </p>
        )}
        {content.subtitle && content.subtitle !== "null" && (
          <p
            className={`${Styles.banner_content__subtitle} ${subtitleClasses}`}
            style={{ color: subtitleTextColor, backgroundColor: subtitleBackgroundColor }}
          >
            {content.subtitle}
          </p>
        )}
        <div className={Styles.banner_content__buttons}>
          {content.links.map((button) => (
            <ALLink
              key={`banner_content_link_${button.link.link}`}
              to={button.link.link}
              className={Styles.banner_content__button}
            >
              <ALButton
                variant="secondary"
                size="medium"
                fullWidth
                buttonBackgroundColor={button.buttonBackgroundColor}
                buttonTextColor={button.buttonTextColor}
                onClick={() => onLinkClick(button.title)}
              >
                {button.title}
              </ALButton>
            </ALLink>
          ))}
        </div>
      </div>
    </div>
  );
}

Banner.propTypes = {
  content: PropTypes.shape({
    contentful_id: PropTypes.string,
    contentVerticalPosition: PropTypes.string,
    contentHorizontalPosition: PropTypes.string,
    contentPosition: PropTypes.string,
    title: PropTypes.string,
    titleSize: PropTypes.arrayOf(PropTypes.string),
    titleTextColor: PropTypes.shape({
      mobile: PropTypes.string,
      desktop: PropTypes.string,
    }),
    titleBgColor: PropTypes.shape({
      mobile: PropTypes.string,
      desktop: PropTypes.string,
    }),
    subtitle: PropTypes.string,
    subtitleSize: PropTypes.arrayOf(PropTypes.string),
    subtitleTextColor: PropTypes.shape({
      mobile: PropTypes.string,
      desktop: PropTypes.string,
    }),
    subtitleBgColor: PropTypes.shape({
      mobile: PropTypes.string,
      desktop: PropTypes.string,
    }),
    subtitlePosition: PropTypes.string,
    backgroundColors: PropTypes.shape({
      mobile: PropTypes.string,
      desktop: PropTypes.string,
    }),
    textColors: PropTypes.shape({
      mobile: PropTypes.string,
      desktop: PropTypes.string,
    }),
    img: PropTypes.shape({
      mobileImage: PropTypes.shape({
        gatsbyImageData: PropTypes.shape({}),
      }),
      desktopImage: PropTypes.shape({
        gatsbyImageData: PropTypes.shape({}),
      }),
      altText: PropTypes.string,
    }),
    mobileVideoThumbnail: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    mobileVideoId: PropTypes.string,
    desktopVideoId: PropTypes.string,
    mobileVideo: PropTypes.shape({
      video: PropTypes.shape({
        filename: PropTypes.string,
        url: PropTypes.string,
      }),
      thumbnail: PropTypes.shape({
        url: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
    }),
    desktopVideo: PropTypes.shape({
      video: PropTypes.shape({
        filename: PropTypes.string,
        url: PropTypes.string,
      }),
      thumbnail: PropTypes.shape({
        url: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
    }),
    links: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        link: PropTypes.shape({
          link: PropTypes.string,
        }),
        buttonBackgroundColor: PropTypes.string,
        buttonTextColor: PropTypes.string,
      })
    ),
  }),
};

export default Banner;
